import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import SelectField from 'components/forms/selectField/SelectField';
import RangePickerField from 'components/forms/rangePickerField/RangePickerField';
import RangeDropdownField from 'components/forms/rangeDropdownField/RangeDropdownField';
import { endOfDay } from 'date-fns';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  getRangeFromPresetDateRange,
  getTimeSpanFromPresetDateRange,
  PresetDateRange,
} from 'utils/dateTimeUtils';
import useHostQualityFilter from 'pages/analyticsAndHostQuality/hostQuality/useHostQualityFilter';
import { LeadChannel } from 'models/Leads';
import Button from 'components/button/Button';

const MAX_DAY_FOR_CUSTOM_DATE_RANGE = 90;

const HostQualityFilters = () => {
  const { t } = useTranslation();
  const { presetDateRange } = useWatch();
  const { setValue, reset } = useFormContext();
  const { hostQualityFilter } = useHostQualityFilter();

  useEffect(() => {
    const range = getRangeFromPresetDateRange(presetDateRange);

    if (range) {
      setValue('range', {
        startDate: range[0],
        endDate: range[1],
      });

      setValue('timeSpan', getTimeSpanFromPresetDateRange(presetDateRange));
    }
  }, [presetDateRange]);

  const onRangeUpdated = (dates: Date[]) => {
    setValue('range', {
      startDate: dates[0],
      endDate: dates[1] ? endOfDay(dates[1]) : undefined,
    });

    setValue('timeSpan', getTimeSpanFromPresetDateRange(presetDateRange));
  };

  return (
    <Row>
      <Col xs={12}>
        <Row>
          <Col sm={6} md={3} lg={2}>
            <RangeDropdownField name="presetDateRange" />
          </Col>
          <Col sm={6} md={3} lg={2}>
            <RangePickerField
              name="rangePicker"
              onChange={onRangeUpdated}
              maxDayRange={MAX_DAY_FOR_CUSTOM_DATE_RANGE}
              disabled={presetDateRange !== PresetDateRange.CUSTOM}
              startDateFieldName="range.startDate"
              endDateFieldName="range.endDate"
            />
          </Col>
          <Col sm={6} md={2} lg={2}>
            <SelectField
              colSmInput={12}
              containerClassName="locations-container"
              name="location"
              options={[
                { label: t('pageAnalytics.filters.allLocations'), value: '' },
                ...(hostQualityFilter?.locations.map((location) => ({
                  label: location,
                  value: location,
                })) || []),
              ]}
            />
          </Col>
          <Col sm={6} md={2} lg={2}>
            <SelectField
              colSmInput={12}
              containerClassName="owners-container"
              name="ownerUid"
              options={[
                { label: t('pageAnalytics.filters.allOwners'), value: '' },
                ...(hostQualityFilter?.owners.map((owner) => ({
                  label: owner.name,
                  value: owner.uid,
                })) || []),
              ]}
            />
          </Col>
          <Col sm={6} md={3} lg={2}>
            <SelectField
              colSmInput={12}
              containerClassName="property-container"
              name="propertyUid"
              options={[
                {
                  label: t('pageAnalytics.filters.allProperties'),
                  value: '',
                },
                ...(hostQualityFilter?.properties
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((property) => ({
                    label: property.name,
                    value: property.uid,
                  })) || []),
              ]}
            />
          </Col>
          <Col sm={6} md={2} lg={2}>
            <SelectField
              colSmInput={12}
              name="channel"
              disabled
              options={[{ label: 'Airbnb', value: LeadChannel.AIRBNB }]}
            />
          </Col>
        </Row>
      </Col>
      <Col xs={12}>
        <Row>
          <Col xs={12}>
            <div className="pull-right">
              <Button bsStyle="link" onClick={() => reset()}>
                {t('pageAnalytics.clearFilter')}
              </Button>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default HostQualityFilters;
